import { Button, Flex, Heading } from "@chakra-ui/react";
import { FieldArray, useFormikContext } from "formik";
import { SelectControl } from "formik-chakra-ui";
import { RiAddLine, RiDeleteBin6Line } from "react-icons/ri";
import { InputControlWithMaxLength } from "src/components/Inputs/InputControlWithMaxLength";
import { LexicalControlWithMaxLength } from "src/components/Inputs/LexicalControlWithMaxLength";
import { AdminCard } from "src/components/Layout/AdminCard";
import { ConditionalTooltip } from "src/components/Layout/ConditionalTooltip";
import { useDeleteConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { isNonEmptyString } from "src/services/predicates";
import {
  FormikType,
  getDefaultStaffContent,
  MAX_BUTTON_TEXT_LENGTH,
  MAX_CUSTOM_CONTENT_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  MAX_TITLE_LENGTH,
  StaffContent,
  StaffContentKind,
} from "./Formik";

type StaffOption = { value: StaffContentKind; label: string };
const staffCardOptions: StaffOption[] = [
  {
    value: "staff-login-button",
    label: "Staff log in screen",
  },
  {
    value: "custom-content",
    label: "Custom content",
  },
];

function isStaffOptionAvailable(
  value: string,
  index: number,
  formikValue: FormikType
): boolean {
  if (value === formikValue.staffCard.contents[index]?.kind) {
    return true;
  }

  const alreadyUsed =
    formikValue.staffCard.contents.findIndex(
      (content) => content.kind === value
    ) >= 0;
  return !alreadyUsed;
}

function firstAvailableStaffOption(
  formikValue: FormikType
): StaffContentKind | null {
  return (
    staffCardOptions.find((option) =>
      isStaffOptionAvailable(option.value, -1, formikValue)
    )?.value ?? null
  );
}
export const StaffCard = () => {
  const formik = useFormikContext<FormikType>();
  const addStaffContentButtonDisabled = !isNonEmptyString(
    firstAvailableStaffOption(formik.values)
  );
  const { confirm, confirmationDialog } = useDeleteConfirmationDialog({
    header: "Are you sure you want to delete this content?",
  });
  return (
    <AdminCard
      title={
        <Heading as="h3" variant="admin">
          Staff welcome card
        </Heading>
      }
      description="Provide guidance to staff who may be logged out."
    >
      <Flex direction="column" gap="4">
        <InputControlWithMaxLength
          label="Title"
          id="staffCard.title"
          name="staffCard.title"
          maxLength={MAX_TITLE_LENGTH}
          inputProps={{
            placeholder: "E.g.: Staff",
          }}
        />
        {/* We don't support rich content for description yet, but I foresee that this request will come soon, so I'm preparing for it by using lexical with hidden toolbar for now. */}
        <LexicalControlWithMaxLength
          label="Description"
          name="staffCard.description"
          maxLength={MAX_DESCRIPTION_LENGTH}
          plainFieldName="staffCard.plainDescription"
          placeholder="E.g.: Administrators, school leaders and team members."
          format="html"
          height="5rem"
          settings={{
            hideToolbar: true,
            enableSmsStats: false,
            enableVariables: false,
            enableAutofocus: false,
            enableBlockFormat: false,
            enableElementFormat: false,
            enableTextFormat: false,
            enableImage: false,
            enableCode: false,
            enableHorizontalRule: false,
            enableLink: false,
          }}
        />
        <FieldArray name="staffCard.contents">
          {(arrayHelpers) => (
            <>
              {formik.values.staffCard.contents.map((content, index) => (
                <AdminCard title="" key={index}>
                  <Flex direction="column" gap="4">
                    <SelectControl
                      label="Destination"
                      name={`staffCard.contents.${index}.kind`}
                      selectProps={{
                        onChange: (event) => {
                          const kind = event.target.value as StaffContentKind;
                          arrayHelpers.replace(index, {
                            kind,
                            content: getDefaultStaffContent(kind),
                            plainContent: "",
                          });
                        },
                      }}
                    >
                      {staffCardOptions.map((option) =>
                        isStaffOptionAvailable(
                          option.value,
                          index,
                          formik.values
                        ) ? (
                          <option value={option.value} key={option.value}>
                            {option.label}
                          </option>
                        ) : null
                      )}
                    </SelectControl>
                    {content.kind === "custom-content" ? (
                      <LexicalControlWithMaxLength
                        format="html"
                        label="Content"
                        name={`staffCard.contents.${index}.content`}
                        plainFieldName={`staffCard.contents.${index}.plainContent`}
                        width="100%"
                        height="5rem"
                        editorVersion="full"
                        settings={{
                          enableSmsStats: false,
                          enableVariables: false,
                          enableAutofocus: false,
                          enableBlockFormat: false,
                          enableElementFormat: false,
                          enableTextFormat: false,
                          enableImage: false,
                          enableCode: false,
                          enableHorizontalRule: false,
                        }}
                        maxLength={MAX_CUSTOM_CONTENT_LENGTH}
                      />
                    ) : (
                      <InputControlWithMaxLength
                        label="Text"
                        name={`staffCard.contents.${index}.content`}
                        maxLength={MAX_BUTTON_TEXT_LENGTH}
                      />
                    )}
                    {index > 0 ? (
                      <Button
                        leftIcon={<RiDeleteBin6Line />}
                        variant="outline"
                        alignSelf="flex-end"
                        colorScheme="gray"
                        onClick={async () => {
                          if (!(await confirm())) {
                            return;
                          }
                          arrayHelpers.remove(index);
                        }}
                      >
                        Remove content
                      </Button>
                    ) : null}
                  </Flex>
                </AdminCard>
              ))}
              <ConditionalTooltip
                label="Maximum amount of content reached"
                showTooltip={addStaffContentButtonDisabled}
              >
                <Button
                  isDisabled={addStaffContentButtonDisabled}
                  leftIcon={<RiAddLine />}
                  variant="outline"
                  colorScheme="gray"
                  borderStyle="dashed"
                  onClick={() => {
                    const kind = firstAvailableStaffOption(formik.values);
                    if (isNonEmptyString(kind)) {
                      arrayHelpers.insert(
                        formik.values.staffCard.contents.length,
                        {
                          kind,
                          content: getDefaultStaffContent(kind),
                          plainContent: "",
                        } satisfies StaffContent
                      );
                    }
                  }}
                >
                  Add content
                </Button>
              </ConditionalTooltip>
            </>
          )}
        </FieldArray>
      </Flex>
      {confirmationDialog}
    </AdminCard>
  );
};
